import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { Calendar as CalendarIcon } from 'lucide-react';

const PRESETS = [
  { value: 'current_month', label: 'Current Month' },
  { value: 'previous_month', label: 'Previous Month' },
  { value: 'current_quarter', label: 'Current Quarter' },
  { value: 'previous_quarter', label: 'Previous Quarter' },
  { value: 'current_year', label: 'Current Year' },
  { value: 'previous_year', label: 'Previous Year' },
  { value: 'custom', label: 'Custom Range…' },
];

/**
 * Combined date-range picker. One `value` object:
 *   { period: "current_month" | ..., startDate?: "YYYY-MM-DD", endDate?: "YYYY-MM-DD" }
 *
 * When `period === "custom"`, the custom start/end dates are used. Otherwise the
 * preset drives the backend range.
 */
export const DateRangePicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [tempStart, setTempStart] = useState(value?.startDate || '');
  const [tempEnd, setTempEnd] = useState(value?.endDate || '');

  const isCustom = value?.period === 'custom';
  const displayLabel = isCustom && value.startDate && value.endDate
    ? `${value.startDate} → ${value.endDate}`
    : (PRESETS.find((p) => p.value === value?.period)?.label || 'Current Month');

  const handleSelect = (next) => {
    if (next === 'custom') {
      setOpen(true);
      return;
    }
    onChange({ period: next, startDate: undefined, endDate: undefined });
  };

  const applyCustom = () => {
    if (!tempStart || !tempEnd) return;
    if (tempStart > tempEnd) return;
    onChange({ period: 'custom', startDate: tempStart, endDate: tempEnd });
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-2" data-testid="date-range-picker">
      <CalendarIcon className="h-4 w-4 text-slate-400" />
      <Select value={value?.period || 'current_month'} onValueChange={handleSelect}>
        <SelectTrigger className="w-56" data-testid="date-range-preset">
          <SelectValue>{displayLabel}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {PRESETS.map((p) => (
            <SelectItem key={p.value} value={p.value} data-testid={`preset-${p.value}`}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={isCustom ? 'default' : 'outline'}
            size="sm"
            className={isCustom ? '' : 'text-slate-500'}
            data-testid="custom-range-btn"
          >
            {isCustom ? 'Edit range' : 'Custom…'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 space-y-3" align="end">
          <p className="text-sm font-medium">Custom range</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">From</Label>
              <Input
                type="date"
                value={tempStart}
                onChange={(e) => setTempStart(e.target.value)}
                data-testid="custom-range-start"
              />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input
                type="date"
                value={tempEnd}
                onChange={(e) => setTempEnd(e.target.value)}
                data-testid="custom-range-end"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={applyCustom}
              disabled={!tempStart || !tempEnd || tempStart > tempEnd}
              data-testid="custom-range-apply"
            >
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

/**
 * Build the query string from a DateRangePicker value for backend calls.
 * Returns URLSearchParams instance so callers can append more keys.
 */
export const toQueryParams = (value) => {
  const p = new URLSearchParams();
  if (value?.period === 'custom' && value.startDate && value.endDate) {
    p.append('start_date', value.startDate);
    p.append('end_date', value.endDate);
  } else if (value?.period) {
    p.append('period', value.period);
  }
  return p;
};
